import React, { useEffect } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Address } from "../types/types";
import { GoogleMap, useJsApiLoader, Marker } from "@react-google-maps/api";
import "./EventCard.css";

const GOOGLE_MAP_LIBRARIES: (
  | "places"
  | "drawing"
  | "geometry"
  | "visualization"
)[] = ["places"];

interface AddressCardProps {
  visible: boolean;
  nodeAddress: Address;
  setShowAddressCard: React.Dispatch<React.SetStateAction<boolean>>;
  handleAddUpdateAddress: (
    id: string,
    nodeTitle: string,
    name: string,
    street: string,
    zip_code: string,
    city: string,
    Phone: string,
    email: string,
    vat: string,
    employees: string,
    notes: string,
    latitude?: number,
    longitude?: number
  ) => void;
  handleDeleteAddress: (id: string) => void;
}

const AddressCard: React.FC<AddressCardProps> = ({
  visible,
  nodeAddress,
  setShowAddressCard: setShowDialog,
  handleAddUpdateAddress,
  handleDeleteAddress,
}) => {
  const [id, setId] = React.useState<string>("");
  const [name, setName] = React.useState<string>("");
  const [street, setStreet] = React.useState<string>("");
  const [zip_code, setZipCode] = React.useState<string>("");
  const [city, setCity] = React.useState<string>("");
  const [Phone, setPhone] = React.useState<string>("");
  const [email, setEmail] = React.useState<string>("");
  const [vat, setVat] = React.useState<string>("");
  const [employees, setEmployees] = React.useState<string>("");
  const [notes, setNotes] = React.useState<string>("");
  const [latitude, setLatitude] = React.useState<number | undefined>(undefined);
  const [longitude, setLongitude] = React.useState<number | undefined>(
    undefined
  );
  const [address, setAddress] = React.useState<string>("");
  const [predictions, setPredictions] = React.useState<any[]>([]);
  const [showDropdown, setShowDropdown] = React.useState<boolean>(false);

  useEffect(() => {
    setId(nodeAddress?.id || "");
    setName(nodeAddress?.name || "");
    setStreet(nodeAddress?.street || "");
    setZipCode(nodeAddress?.zip_code || "");
    setCity(nodeAddress?.city || "");
    setPhone(nodeAddress?.Phone || "");
    setEmail(nodeAddress?.email || "");
    setVat(nodeAddress?.vat || "");
    setEmployees(nodeAddress?.employees || "");
    setNotes(nodeAddress?.notes || "");
    setLatitude(nodeAddress?.latitude);
    setLongitude(nodeAddress?.longitude);
  }, [nodeAddress]);

  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_MAP_LIBRARIES,
  });

  return (
    <Dialog
      header={id ? "Edit Address" : "Add Address"}
      visible={visible}
      style={{ width: "500px" }}
      onHide={() => setShowDialog(false)}
      footer={
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          {id && (
            <Button
              label="Delete"
              icon="pi pi-trash"
              className="p-button-danger p-button-text"
              style={{ alignSelf: "flex-start" }}
              onClick={() => {
                handleDeleteAddress(id);
                setShowDialog(false);
              }}
            />
          )}
          <span style={{ marginLeft: "auto" }}>
            <Button
              label="Cancel"
              icon="pi pi-times"
              className="p-button-text"
              onClick={() => setShowDialog(false)}
            />
            <Button
              label="Save"
              icon="pi pi-check"
              onClick={() => {
                handleAddUpdateAddress(
                  id,
                  nodeAddress?.nodeTitle || "",
                  name,
                  street,
                  zip_code,
                  city,
                  Phone,
                  email,
                  vat,
                  employees,
                  notes,
                  latitude,
                  longitude
                );
                setShowDialog(false);
                setAddress("");
              }}
            />
          </span>
        </div>
      }
    >
      <div className="p-fluid">
        {/* Google Maps Autocomplete Field */}
        {isLoaded && (
          <div className="p-field" style={{ position: "relative" }}>
            <label htmlFor="address-autocomplete">Search Address</label>
            <InputText
              id="address-autocomplete"
              type="text"
              style={{ fontSize: "1rem", width: "95%" }}
              value={address}
              onChange={async (e) => {
                setAddress(e.target.value);
                if (e.target.value.length > 2) {
                  const service =
                    new window.google.maps.places.AutocompleteService();
                  service.getPlacePredictions(
                    { input: e.target.value, types: ["address"] },
                    (predictions: any[] | null) => {
                      setPredictions(predictions || []);
                      setShowDropdown(true);
                    }
                  );
                } else {
                  setPredictions([]);
                  setShowDropdown(false);
                }
              }}
              autoComplete="off"
              onFocus={() => {
                if (predictions.length > 0) setShowDropdown(true);
              }}
              onBlur={() => {
                setTimeout(() => setShowDropdown(false), 200);
              }}
            />
            {showDropdown && predictions.length > 0 && (
              <ul
                style={{
                  position: "absolute",
                  zIndex: 1000,
                  background: "#fff",
                  width: "95%",
                  border: "1px solid #ccc",
                  margin: 0,
                  padding: 0,
                  listStyle: "none",
                  maxHeight: 200,
                  overflowY: "auto",
                }}
              >
                {predictions.map((prediction: any) => (
                  <li
                    key={prediction.place_id}
                    style={{
                      padding: "8px",
                      cursor: "pointer",
                      borderBottom: "1px solid #eee",
                    }}
                    onMouseDown={async () => {
                      setAddress(prediction.description);
                      setShowDropdown(false);
                      // Get details for selected place
                      const geocoder = new window.google.maps.Geocoder();
                      geocoder.geocode(
                        { placeId: prediction.place_id },
                        (results: any[], status: string) => {
                          if (
                            status === "OK" &&
                            results &&
                            results.length > 0
                          ) {
                            const place = results[0];
                            const getComponent = (type: string) =>
                              place.address_components?.find((c: any) =>
                                c.types.includes(type)
                              )?.long_name || "";
                            setStreet(
                              getComponent("route") ||
                                getComponent("street_address")
                            );
                            setZipCode(getComponent("postal_code"));
                            setCity(
                              getComponent("locality") ||
                                getComponent("postal_town") ||
                                getComponent("administrative_area_level_2")
                            );
                            setLatitude(place.geometry?.location?.lat());
                            setLongitude(place.geometry?.location?.lng());
                          }
                        }
                      );
                    }}
                  >
                    {prediction.description}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        <div className="p-field">
          <label htmlFor="address-name">Name</label>
          <InputText
            id="address-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
            style={{ fontSize: "1rem", width: "95%" }}
          />
        </div>
        <div className="p-field">
          <label htmlFor="address-street">Street</label>
          <InputText
            id="address-street"
            value={street}
            onChange={(e) => setStreet(e.target.value)}
            style={{ fontSize: "1rem", width: "95%" }}
          />
        </div>
        <div className="p-field">
          <label htmlFor="address-zip">ZIP Code</label>
          <InputText
            id="address-zip"
            value={zip_code}
            onChange={(e) => setZipCode(e.target.value)}
            style={{ fontSize: "1rem", width: "95%" }}
          />
        </div>
        <div className="p-field">
          <label htmlFor="address-city">City</label>
          <InputText
            id="address-city"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            style={{ fontSize: "1rem", width: "95%" }}
          />
        </div>
        <div className="p-field">
          <label htmlFor="address-phone">Phone</label>
          <InputText
            id="address-phone"
            value={Phone}
            onChange={(e) => setPhone(e.target.value)}
            style={{ fontSize: "1rem", width: "95%" }}
          />
        </div>
        <div className="p-field">
          <label htmlFor="address-email">Email</label>
          <InputText
            id="address-email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={{ fontSize: "1rem", width: "95%" }}
          />
        </div>
        <div className="p-field">
          <label htmlFor="address-vat">VAT</label>
          <InputText
            id="address-vat"
            value={vat}
            onChange={(e) => setVat(e.target.value)}
            style={{ fontSize: "1rem", width: "95%" }}
          />
        </div>
        <div className="p-field">
          <label htmlFor="address-employees">Employees</label>
          <InputText
            id="address-employees"
            value={employees}
            onChange={(e) => setEmployees(e.target.value)}
            style={{ fontSize: "1rem", width: "95%" }}
          />
        </div>
        <div className="p-field">
          <label htmlFor="address-notes">Notes</label>
          <InputText
            id="address-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            style={{ fontSize: "1rem", width: "95%" }}
          />
        </div>
        <div className="p-field">
          <label htmlFor="address-latitude">Latitude</label>
          <InputText
            id="address-latitude"
            value={latitude !== undefined ? String(latitude) : ""}
            onChange={(e) =>
              setLatitude(e.target.value ? Number(e.target.value) : undefined)
            }
            style={{ fontSize: "1rem", width: "95%" }}
          />
        </div>
        <div className="p-field">
          <label htmlFor="address-longitude">Longitude</label>
          <InputText
            id="address-longitude"
            value={longitude !== undefined ? String(longitude) : ""}
            onChange={(e) =>
              setLongitude(e.target.value ? Number(e.target.value) : undefined)
            }
            style={{ fontSize: "1rem", width: "95%" }}
          />
        </div>
      </div>
    </Dialog>
  );
};

export default AddressCard;
